import { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid2';
import Pagination from '@mui/material/Pagination';
import { styled } from '@mui/material/styles';
import { isEmpty, keys, omit, pickBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import ButtonSet from './ButtonSet';
import FacetingMenu from './FacetingMenu';
import ResultCard from './ResultCard';
import { DEFAULT_SEARCH_ROW } from '../../../constants';
import { useSearch } from '../../../contexts/SearchContext';
import { sentenceCase } from '../../../utils/utils';
import Loading from '../../elements/loading/Loading';
import Text from '../../elements/text/Text';
import NoContent from '../../sections/no-content/NoContent';

const StyledInputsContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  containerType: 'inline-size',
  gridTemplateColumns: '1fr 2fr',
}));

const StyledButtonsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '@container (max-width: 500px)': {
    justifyContent: 'center',
  },
}));

const StyledFaceting = styled('div')(() => ({
  '@container (max-width: 500px)': {
    gridColumn: 'span 2',
  },
}));

const StyledResults = styled('div')(() => ({
  '@container (max-width: 500px)': {
    gridColumn: 'span 2',
  },
}));

const SearchResults = () => {
  const { t } = useTranslation();
  const {
    isFetching,
    query,
    error,
    results,
    isPageChanging,
    handlePageChange,
  } = useSearch();
  const [currentPage, setCurrentPage] = useState(1);

  const {
    facet_counts: facetCounts,
    response,
    highlighting = {},
    url,
  } = results;
  const { uniqueKey } = query;

  const pages = Math.ceil(response?.numFound / DEFAULT_SEARCH_ROW);

  const formattedFaceting = (facet) => {
    const cleanedFacet = pickBy(facet, (item) => !isEmpty(keys(item)));

    const extractFacetFields = (facetFields) => {
      const formatArrayToObject = (valueArray) => {
        return valueArray.reduce((prev, current, idx, arr) => {
          if (idx % 2 !== 0 && arr[idx - 1]) {
            prev[arr[idx - 1]] = current;
          }
          return prev;
        }, {});
      };

      return keys(facetFields).reduce((prev, key) => {
        prev[key] = formatArrayToObject(facetFields[key]);
        return prev;
      }, {});
    };

    const extractFacetQueries = (facetQueries) => {
      const querySet = new Set();
      keys(facetQueries).forEach((query) =>
        querySet.add(query.substring(0, query.indexOf(':')))
      );

      return Array.from(querySet).reduce((queryObj, queryKey) => {
        const filteredQueries = pickBy(facetQueries, (_, key) =>
          key.includes(queryKey)
        );
        const formatKeyValuePair = keys(filteredQueries).reduce((obj, key) => {
          const formattedKey = key.replace(/^(.*?):/, '');
          obj[formattedKey] = facetQueries[key];
          return obj;
        }, {});

        queryObj[queryKey] = formatKeyValuePair;
        return queryObj;
      }, {});
    };

    const outputObject = keys(cleanedFacet).reduce((newObj, key) => {
      switch (key) {
        case 'facet_fields':
          return { ...newObj, ...extractFacetFields(cleanedFacet[key]) };
        case 'facet_queries':
          return { ...newObj, ...extractFacetQueries(cleanedFacet[key]) };
        default:
          return newObj;
      }
    }, {});

    return outputObject;
  };

  const formattedDocs = (docs, highlighting) =>
    docs.map((doc) => ({
      ...doc,
      ...(highlighting[doc[uniqueKey]] && {
        highlighting: highlighting[doc[uniqueKey]],
      }),
    }));

  const faceting = formattedFaceting(facetCounts);

  const displayDocs = response
    ? formattedDocs(response.docs, highlighting)
    : [];

  const onPageChange = (_, value) => {
    setCurrentPage(value);
    const newStart = (value - 1) * DEFAULT_SEARCH_ROW;
    handlePageChange(newStart);
  };

  const errorSection = (
    <Box sx={{ my: 2 }}>
      <Alert severity="error">
        {sentenceCase(
          t('loading', {
            ns: 'errors',
            content: t('searchResults', { ns: 'instance' }),
          })
        )}
        <Text>{error}</Text>
      </Alert>
    </Box>
  );

  useEffect(() => {
    if (query?.jsonQuery?.offset !== undefined) {
      setCurrentPage(query?.jsonQuery?.offset / 10 + 1);
    }
  }, [query?.jsonQuery?.offset]);

  return (
    <Container>
      {isFetching ? (
        <Loading />
      ) : error ? (
        errorSection
      ) : isEmpty(query) ? (
        <NoContent
          title={sentenceCase(t('searchNotExecuted', { ns: 'descriptions' }))}
        />
      ) : (
        <StyledInputsContainer>
          <StyledFaceting>
            <FacetingMenu faceting={faceting} />
          </StyledFaceting>
          <StyledResults>
            <StyledButtonsWrapper>
              <ButtonSet json={omit(results, ['url'])} url={url} />
              <Text>
                {t('numberFound', {
                  ns: 'instance',
                  text: response?.numFound.toLocaleString() || 0,
                })}
              </Text>
            </StyledButtonsWrapper>

            {isEmpty(displayDocs) ? (
              <NoContent
                title={sentenceCase(
                  t('notFound', {
                    ns: 'descriptions',
                    text: t('search', { ns: 'titles' }),
                  })
                )}
              />
            ) : (
              <Grid container spacing={1}>
                {displayDocs.map((doc, index) => (
                  <Grid key={index} size={12}>
                    <ResultCard doc={doc} />
                  </Grid>
                ))}
              </Grid>
            )}

            {pages > 1 && (
              <Box sx={{ mt: 2 }}>
                <Pagination
                  count={pages}
                  page={currentPage}
                  shape="rounded"
                  disabled={isPageChanging}
                  onChange={onPageChange}
                />
              </Box>
            )}
          </StyledResults>
        </StyledInputsContainer>
      )}
    </Container>
  );
};

export default SearchResults;
